import { FirebaseOptions, initializeApp } from "firebase/app";
import { getMessaging } from "firebase/messaging";
import { getAuth } from "firebase/auth";
import {
  AppCheck,
  getToken as getAppCheckToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

type RuntimeFirebaseConfig = Partial<FirebaseOptions> & {
  appCheckSiteKey?: string;
};

const APP_CHECK_TOKEN_MAX_ATTEMPTS = 5;
const APP_CHECK_TOKEN_RETRY_DELAY_MS = 750;

const isDevelopmentHost = (): boolean => {
  return process.env.NODE_ENV === "development";
};

const isBrowser = (): boolean => {
  return globalThis.window !== undefined;
};

const getEnvFirebaseConfig = (): RuntimeFirebaseConfig => ({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  appCheckSiteKey: process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY,
});

const hasRequiredFirebaseConfig = (
  config: RuntimeFirebaseConfig,
): config is FirebaseOptions => {
  return Boolean(
    config.apiKey && config.authDomain && config.projectId && config.appId,
  );
};

const envConfig = getEnvFirebaseConfig();

if (!hasRequiredFirebaseConfig(envConfig)) {
  throw new Error(
    "Firebase config is missing. Provide REACT_APP_FIREBASE_* variables.",
  );
}

const firebaseConfig: FirebaseOptions = envConfig;
const app = initializeApp(firebaseConfig);
const appCheckSiteKey = envConfig.appCheckSiteKey;

const isMissingAppCheckRegistrationError = (error: unknown): boolean => {
  return (
    error instanceof Error &&
    error.message.includes("Component app-check has not been registered yet")
  );
};

const logLocalAppCheckToken = async (
  appCheck: AppCheck,
  attemptsLeft = APP_CHECK_TOKEN_MAX_ATTEMPTS,
): Promise<void> => {
  if (
    !isDevelopmentHost() ||
    !isBrowser() ||
    globalThis.window.__FIREBASE_APPCHECK_TOKEN_LOGGED__
  ) {
    return;
  }

  try {
    const tokenResult = await getAppCheckToken(appCheck, true);
    console.info("Firebase App Check token (local):", tokenResult.token);
    globalThis.window.__FIREBASE_APPCHECK_TOKEN_LOGGED__ = true;
  } catch (error) {
    if (attemptsLeft > 1) {
      setTimeout(() => {
        void logLocalAppCheckToken(appCheck, attemptsLeft - 1);
      }, APP_CHECK_TOKEN_RETRY_DELAY_MS);
      return;
    }

    console.warn("Unable to fetch Firebase App Check token locally:", error);
  }
};

const initializeAppCheckOnce = (): AppCheck => {
  const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey as string),
    isTokenAutoRefreshEnabled: true,
  });
  globalThis.window.__FIREBASE_APPCHECK_INITIALIZED__ = true;
  globalThis.window.__FIREBASE_APPCHECK_INSTANCE__ = appCheck;
  return appCheck;
};

const retryAppCheckInitializationOnNextTick = (): void => {
  // During hot-reload, App Check registration can race once. Retry on next tick.
  setTimeout(() => {
    if (!globalThis.window?.__FIREBASE_APPCHECK_INITIALIZED__) {
      try {
        const appCheck = initializeAppCheckOnce();
        void logLocalAppCheckToken(appCheck);
      } catch {
        // Keep local dev quiet for this known transient race.
      }
    }
  }, 0);
};

const ensureAppCheckTokenLogged = (): void => {
  if (
    globalThis.window.__FIREBASE_APPCHECK_INITIALIZED__ &&
    globalThis.window.__FIREBASE_APPCHECK_INSTANCE__ &&
    !globalThis.window.__FIREBASE_APPCHECK_TOKEN_LOGGED__
  ) {
    void logLocalAppCheckToken(
      globalThis.window.__FIREBASE_APPCHECK_INSTANCE__ as AppCheck,
    );
  }
};

const initializeFirebaseAppCheck = (): void => {
  if (!appCheckSiteKey || !isBrowser()) {
    return;
  }

  if (!globalThis.window.__FIREBASE_APPCHECK_INITIALIZED__) {
    try {
      const appCheck = initializeAppCheckOnce();
      void logLocalAppCheckToken(appCheck);
    } catch (error) {
      if (isDevelopmentHost() && isMissingAppCheckRegistrationError(error)) {
        retryAppCheckInitializationOnNextTick();
      } else if (isDevelopmentHost()) {
        console.warn("Firebase App Check failed to initialize locally:", error);
      } else {
        throw error;
      }
    }
  }

  ensureAppCheckTokenLogged();
};

initializeFirebaseAppCheck();

const messaging = getMessaging(app);
const auth = getAuth(app);

export { messaging, auth };
export { getToken } from "firebase/messaging";
